import type { ComputedRef, Ref } from 'vue'
import type { InterestDomain, SelectedInterestDomain } from '~/types/interestDomains'
import type {
  QualificationCriteriaCategory,
  QualificationCriteriaOption,
  QualificationCriteriaQuestion,
  QualificationCriteriaRequest,
} from '~/types/UserQualifications'
import type {
  DimensionThemeProgress,
  ProfileSummary,
  QualificationInterest,
  QualificationLocation,
  QualificationSnapshot,
  RememberedItem,
} from '~/types/NotificationsHub'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import {
  DEGREES_QUESTION,
  DURATION_QUESTION,
  START_DATE_QUESTION,
  STYLES_QUESTION,
} from '~/constants/qualificationCriteria'
import { useMbtiStore } from '~/stores/mbtiStore'
import { useFrontApi } from '~/composables/useFrontApi'

type StoreStatus = 'idle' | 'ready'

type TaggedItem = {
  name: string
  isNew: boolean
}

type TaggedLocation = {
  city: string | null
  country: string | null
  isNew: boolean
}

type KnownProfileNames = {
  cities: Set<string | null>
  interests: Set<string>
  academicLevel: Set<string>
  academicDegrees: Set<string>
  academicDuration: Set<string>
  academicStyles: Set<string>
  academicStartDate: Set<string>
}

type QualificationTaggedCategory = Exclude<QualificationCriteriaCategory, 'Locations'>

// DTO de `GET /api/Profile/qualification/profile` (imbriqué, distinct du snapshot SignalR à plat).
type QualificationInterestDto = {
  name: string | null
}

type QualificationWishDto = {
  degrees: string[] | null
  duration: string | null
  styles: string[] | null
  startDate: string | null
}

type QualificationStudiesDto = {
  currentLevel: string | null
  wish: QualificationWishDto | null
}

type QualificationProfileDto = {
  locations: QualificationLocation[] | null
  interests: QualificationInterestDto[] | null
  studies: QualificationStudiesDto | null
}

type QualificationSummaryDto = {
  summary: string | null
}

type QualificationCriteriaResponse = {
  summary: string | null
  profile: QualificationProfileDto
  generatedQuestion: string | null
}

type QualificationReferentialsDto = {
  degrees: QualificationCriteriaOption[] | null
  styles: QualificationCriteriaOption[] | null
  interestDomains: InterestDomain[] | null
}

// int = valeur de l'enum FrontApi (ordre repris de notre spec de qualification).
const QUALIFICATION_CRITERIA_CATEGORY_ENUM: Record<QualificationCriteriaCategory, number> = {
  Locations: 0,
  Interests: 1,
  AcademicLevel: 2,
  Degrees: 3,
  Duration: 4,
  Styles: 5,
  StartDate: 6,
}

const CONFIDENCE_SCORE_BASELINE_STORAGE_KEY: string = 'chat.confidenceScoreBaseline'

/**
 * @returns {number | null} Score baseline persisté en session (survit au refresh).
 */
function readConfidenceScoreBaseline(): number | null {
  if (!import.meta.client) return null
  try {
    const raw: string | null = sessionStorage.getItem(CONFIDENCE_SCORE_BASELINE_STORAGE_KEY)
    if (raw === null) return null
    const parsed: number = Number(raw)
    return Number.isFinite(parsed) ? Math.round(parsed) : null
  } catch {
    return null
  }
}

/**
 * @param {number} score - Score à mémoriser comme baseline du +N.
 * @returns {void}
 */
function writeConfidenceScoreBaseline(score: number): void {
  if (!import.meta.client) return
  try {
    sessionStorage.setItem(CONFIDENCE_SCORE_BASELINE_STORAGE_KEY, String(Math.round(score)))
  } catch {
    console.error('Error writing confidence score baseline to session storage')
  }
}

/**
 * Collecte les noms des tags déjà en store.
 * @param {TaggedItem[]} items - Tags déjà en store.
 * @returns {Set<string>} Noms déjà connus.
 */
function namesSet(items: TaggedItem[]): Set<string> {
  return new Set(items.map((item: TaggedItem): string => item.name))
}

/**
 * Transforme des libellés en tags store, avec flag `isNew` si le nom n'était pas connu.
 * @param {string[]} names - Libellés du snapshot.
 * @param {Set<string>} alreadyKnown - Noms présents avant ce snapshot.
 * @returns {TaggedItem[]} Tags store.
 */
function profileTags(names: string[], alreadyKnown: Set<string>): TaggedItem[] {
  return names.map((name: string): TaggedItem => ({ name, isNew: !alreadyKnown.has(name) }))
}

/**
 * Aplati le profil de qualification REST vers le snapshot consommé par `setQualification`.
 * @param {QualificationProfileDto} profile - Réponse de `GET /api/Profile/qualification/profile`.
 * @returns {QualificationSnapshot} Snapshot plat (même forme que l'événement SignalR).
 */
function toQualificationSnapshot(profile: QualificationProfileDto): QualificationSnapshot {
  return {
    locations: profile.locations ?? [],
    interests: (profile.interests ?? []).map(
      (interest: QualificationInterestDto): QualificationInterest => ({
        domain: '',
        name: interest.name ?? '',
      }),
    ),
    degrees: profile.studies?.wish?.degrees ?? [],
    studyStyles: profile.studies?.wish?.styles ?? [],
    currentLevel: profile.studies?.currentLevel ?? null,
    studyDuration: profile.studies?.wish?.duration ?? null,
    studyStartDate: profile.studies?.wish?.startDate ?? null,
  }
}

/**
 * Range les intérêts de l'élève par domaine (un libellé commun à deux domaines suit les autres intérêts), le reste du domaine en suggestions.
 * @param {string[]} interestNames - Intérêts du profil.
 * @param {readonly InterestDomain[]} domains - Domaines du référentiel.
 * @returns {SelectedInterestDomain[]} Domaines où l'élève a au moins un intérêt, dans l'ordre du référentiel.
 */
function toSelectedInterestDomains(
  interestNames: string[],
  domains: readonly InterestDomain[],
): SelectedInterestDomain[] {
  const domainsByInterest: Map<string, InterestDomain[]> = new Map(
    interestNames.map((interestName: string): [string, InterestDomain[]] => [
      interestName,
      domains.filter((domain: InterestDomain): boolean => domain.interests.includes(interestName)),
    ]),
  )

  const domainsWithUnambiguousInterest: Set<InterestDomain> = new Set(
    [...domainsByInterest.values()].flatMap((candidates: InterestDomain[]): InterestDomain[] =>
      candidates.length === 1 ? candidates : [],
    ),
  )

  const selectedInterestsByDomain: Map<InterestDomain, string[]> = new Map()
  for (const interestName of interestNames) {
    const candidates: InterestDomain[] = domainsByInterest.get(interestName) ?? []
    const domain: InterestDomain | undefined =
      candidates.find((candidate: InterestDomain): boolean =>
        domainsWithUnambiguousInterest.has(candidate),
      ) ?? candidates[0]

    if (!domain) {
      continue
    }

    selectedInterestsByDomain.set(domain, [
      ...(selectedInterestsByDomain.get(domain) ?? []),
      interestName,
    ])
  }

  return domains
    .filter((domain: InterestDomain): boolean => selectedInterestsByDomain.has(domain))
    .map(
      (domain: InterestDomain): SelectedInterestDomain => ({
        name: domain.name,
        selectedInterests: selectedInterestsByDomain.get(domain) ?? [],
        suggestedInterests: domain.interests.filter(
          (interest: string): boolean => !interestNames.includes(interest),
        ),
      }),
    )
}

export const useAgentInformationsStore = defineStore('agentInformations', () => {
  const summary: Ref<string> = ref('')
  const locations: Ref<TaggedLocation[]> = ref([])
  const interests: Ref<TaggedItem[]> = ref([])
  const academicLevel: Ref<TaggedItem[]> = ref([])
  const academicDegrees: Ref<TaggedItem[]> = ref([])
  const academicDuration: Ref<TaggedItem[]> = ref([])
  const academicStyles: Ref<TaggedItem[]> = ref([])
  const academicStartDate: Ref<TaggedItem[]> = ref([])
  const remembered: Ref<RememberedItem[]> = ref([])
  const dimensions: Ref<DimensionThemeProgress[]> = ref([])
  const status: Ref<StoreStatus> = ref('idle')
  const referentials: Ref<QualificationReferentialsDto | null> = ref(null)
  const hasUnseenInterests: Ref<boolean> = ref(false)

  const itemsByCategory: Record<QualificationTaggedCategory, Ref<TaggedItem[]>> = {
    Interests: interests,
    AcademicLevel: academicLevel,
    Degrees: academicDegrees,
    Duration: academicDuration,
    Styles: academicStyles,
    StartDate: academicStartDate,
  }

  // MBTI dérivé du store personnalité : le hub le pousse via mbtiStore, pas via la qualification.
  const mbtiProfile: ComputedRef<TaggedItem[]> = computed((): TaggedItem[] => {
    const mbtiCode: string | null = useMbtiStore().mbti
    return mbtiCode ? [{ name: mbtiCode, isNew: false }] : []
  })

  const degreesQuestion: ComputedRef<QualificationCriteriaQuestion> = computed(
    (): QualificationCriteriaQuestion => ({
      ...DEGREES_QUESTION,
      options: referentials.value?.degrees ?? [],
    }),
  )

  const stylesQuestion: ComputedRef<QualificationCriteriaQuestion> = computed(
    (): QualificationCriteriaQuestion => ({
      ...STYLES_QUESTION,
      options: referentials.value?.styles ?? [],
    }),
  )

  const unansweredCriteriaQuestions: ComputedRef<QualificationCriteriaQuestion[]> = computed(
    (): QualificationCriteriaQuestion[] =>
      [degreesQuestion.value, DURATION_QUESTION, stylesQuestion.value, START_DATE_QUESTION].filter(
        (question: QualificationCriteriaQuestion): boolean =>
          question.options.length > 0 && itemsByCategory[question.category].value.length === 0,
      ),
  )

  const selectedInterestDomains: ComputedRef<SelectedInterestDomain[]> = computed(
    (): SelectedInterestDomain[] =>
      toSelectedInterestDomains(
        interests.value.map((interest: TaggedItem): string => interest.name),
        referentials.value?.interestDomains ?? [],
      ),
  )

  const filledCriteriaCategoryCount: ComputedRef<number> = computed((): number => {
    const isCriteriaCategoryFilled: boolean[] = [
      locations.value.some((location: TaggedLocation): boolean => Boolean(location.city)),
      locations.value.some((location: TaggedLocation): boolean => Boolean(location.country)),
      interests.value.length > 0,
      academicLevel.value.length > 0,
      academicDegrees.value.length > 0,
      academicDuration.value.length > 0,
      academicStyles.value.length > 0,
      academicStartDate.value.length > 0,
      mbtiProfile.value.length > 0,
    ]

    return isCriteriaCategoryFilled.filter(Boolean).length
  })

  /**
   * Noms déjà en store, pour taguer les nouveautés au prochain snapshot.
   * @returns {KnownProfileNames} Ensembles de noms connus.
   */
  function snapshotKnownNames(): KnownProfileNames {
    return {
      cities: new Set(
        locations.value.map((location: TaggedLocation): string | null => location.city),
      ),
      interests: namesSet(interests.value),
      academicLevel: namesSet(academicLevel.value),
      academicDegrees: namesSet(academicDegrees.value),
      academicDuration: namesSet(academicDuration.value),
      academicStyles: namesSet(academicStyles.value),
      academicStartDate: namesSet(academicStartDate.value),
    }
  }

  /**
   * Définit le résumé « Ce que j'ai compris de toi » (SignalR `SummaryChanged`).
   * @param {ProfileSummary} payload - Résumé courant.
   * @returns {void}
   */
  function setSummary(payload: ProfileSummary): void {
    summary.value = payload
  }

  /**
   * Définit les critères de qualification (SignalR `QualificationChanged`), en taguant les nouveautés.
   * @param {QualificationSnapshot} payload - État complet des critères déduits.
   * @returns {void}
   */
  function setQualification(payload: QualificationSnapshot): void {
    const known: KnownProfileNames = snapshotKnownNames()

    locations.value = payload.locations.map(
      (location: QualificationLocation): TaggedLocation => ({
        city: location.city,
        country: location.country,
        isNew: !known.cities.has(location.city),
      }),
    )
    interests.value = profileTags(
      payload.interests.map((interest: QualificationInterest): string => interest.name),
      known.interests,
    )
    academicLevel.value = profileTags(
      payload.currentLevel ? [payload.currentLevel] : [],
      known.academicLevel,
    )
    academicDegrees.value = profileTags(payload.degrees, known.academicDegrees)
    academicDuration.value = profileTags(
      payload.studyDuration ? [payload.studyDuration] : [],
      known.academicDuration,
    )
    academicStyles.value = profileTags(payload.studyStyles, known.academicStyles)
    academicStartDate.value = profileTags(
      payload.studyStartDate ? [payload.studyStartDate] : [],
      known.academicStartDate,
    )

    status.value = 'ready'
  }

  /**
   * Applique les critères poussés par Lola (SignalR) et allume la pastille bleue des filtres si un intérêt est arrivé.
   * @param {QualificationSnapshot} payload - État complet des critères déduits.
   * @returns {void}
   */
  function applyQualificationFromLola(payload: QualificationSnapshot): void {
    const knownInterestNames: Set<string> = namesSet(interests.value)
    const hasNewInterest: boolean = payload.interests.some(
      (interest: QualificationInterest): boolean => !knownInterestNames.has(interest.name),
    )
    const isProfileLoaded: boolean = status.value === 'ready'

    setQualification(payload)

    if (isProfileLoaded && hasNewInterest) {
      hasUnseenInterests.value = true
    }
  }

  /**
   * Éteint la pastille bleue des filtres, à l'ouverture du tiroir.
   * @returns {void}
   */
  function markInterestsAsSeen(): void {
    hasUnseenInterests.value = false
  }

  /**
   * Fusionne la progression des dimensions par thème (SignalR `DimensionProgressChanged`, delta partiel).
   * @param {DimensionThemeProgress[]} payload - Thèmes impactés par la dernière question.
   * @returns {void}
   */
  function applyDimensions(payload: DimensionThemeProgress[]): void {
    const byId: Map<string, DimensionThemeProgress> = new Map(
      dimensions.value.map((theme: DimensionThemeProgress): [string, DimensionThemeProgress] => [
        theme.id,
        theme,
      ]),
    )
    for (const theme of payload) byId.set(theme.id, theme)
    dimensions.value = [...byId.values()]
  }

  /**
   * Définit la liste des éléments retenus (SignalR `RememberedItemsChanged`, snapshot complet).
   * @param {RememberedItem[]} payload - Liste complète et à jour.
   * @returns {void}
   */
  function setRemembered(payload: RememberedItem[]): void {
    remembered.value = payload
  }

  /**
   * Charge le profil initial (qualification, résumé, dimensions) au montage du chat.
   * @returns {Promise<void>}
   */
  async function fetchProfile(): Promise<void> {
    const frontApi = useFrontApi()
    const [profile, summary, themes] = await Promise.allSettled([
      frontApi.get<QualificationProfileDto>('/api/Profile/qualification/profile'),
      frontApi.get<QualificationSummaryDto>('/api/Profile/qualification/summary'),
      frontApi.get<DimensionThemeProgress[]>('/api/dimensions/me'),
    ])
    if (profile.status === 'fulfilled')
      setQualification(toQualificationSnapshot(profile.value.data))
    if (summary.status === 'fulfilled') setSummary(summary.value.data.summary ?? '')
    if (themes.status === 'fulfilled') applyDimensions(themes.value.data)
  }

  /**
   * Charge les référentiels des filtres (diplômes, styles, domaines d'intérêts) au montage du chat.
   * @returns {Promise<void>}
   */
  async function fetchReferentials(): Promise<void> {
    try {
      const { data } = await useFrontApi().get<QualificationReferentialsDto>(
        '/api/Profile/qualification/referentials',
      )
      referentials.value = data
    } catch (error: unknown) {
      console.error('[agentInformationsStore] fetchReferentials:', error)
    }
  }

  /**
   * Retire un critère localement.
   * @param {QualificationCriteriaCategory} category - Catégorie contrat.
   * @param {string} value - Valeur à retirer (ville, diplôme…).
   * @returns {void}
   */
  function removeCriterionLocally(category: QualificationCriteriaCategory, value: string): void {
    if (category === 'Locations') {
      locations.value = locations.value.filter(
        (location: TaggedLocation): boolean =>
          location.city !== value && location.country !== value,
      )
      return
    }

    const items: Ref<TaggedItem[]> = itemsByCategory[category]
    items.value = items.value.filter((item: TaggedItem): boolean => item.name !== value)
  }

  /**
   * Ajoute un critère choisi dans le drawer filtres : ajout optimiste, puis PUT FrontApi réconcilié sur la réponse.
   * @param {QualificationTaggedCategory} category - Catégorie contrat.
   * @param {string} value - Valeur choisie (libellé du référentiel).
   * @param {boolean} isMultipleChoice - `true` : la valeur s'ajoute aux autres ; `false` : elle remplace l'existante.
   * @returns {Promise<void>}
   */
  async function addCriterion(
    category: QualificationTaggedCategory,
    value: string,
    isMultipleChoice: boolean,
  ): Promise<void> {
    const items: Ref<TaggedItem[]> = itemsByCategory[category]
    const chosenItem: TaggedItem = { name: value, isNew: false }
    items.value = isMultipleChoice ? [...items.value, chosenItem] : [chosenItem]

    const body: QualificationCriteriaRequest = {
      category: QUALIFICATION_CRITERIA_CATEGORY_ENUM[category],
      value,
    }
    try {
      const { data } = await useFrontApi().put<QualificationCriteriaResponse>(
        '/api/Profile/qualification/criteria',
        body,
      )
      setQualification(toQualificationSnapshot(data.profile))
      setSummary(data.summary ?? '')
    } catch (error: unknown) {
      console.error('[agentInformationsStore] addCriterion:', error)
      await fetchProfile()
    }
  }

  /**
   * Supprime un critère déduit : retrait optimiste, puis DELETE FrontApi réconcilié sur la réponse.
   * @param {QualificationCriteriaCategory} category - Catégorie contrat.
   * @param {string} value - Valeur à retirer.
   * @returns {Promise<void>}
   */
  async function deleteCriterion(
    category: QualificationCriteriaCategory,
    value: string,
  ): Promise<void> {
    removeCriterionLocally(category, value)
    const body: QualificationCriteriaRequest = {
      category: QUALIFICATION_CRITERIA_CATEGORY_ENUM[category],
      value,
    }
    try {
      const { data } = await useFrontApi().delete<QualificationCriteriaResponse>(
        '/api/Profile/qualification/criteria',
        { data: body },
      )
      setQualification(toQualificationSnapshot(data.profile))
      setSummary(data.summary ?? '')
      // @Todo generatedQuestion : relancer Lola quand une catégorie se vide (UX à définir).
    } catch (error: unknown) {
      // Échec de l'appel : recharge l'état authoritative, ce qui annule le retrait optimiste.
      console.error('[agentInformationsStore] deleteCriterion:', error)
      await fetchProfile()
    }
  }

  /**
   * Retire tous les intérêts d'un domaine : retrait optimiste, un DELETE par intérêt, réconciliation sur la dernière réponse.
   * @param {SelectedInterestDomain} interestDomain - Domaine retiré (poubelle de sa carte).
   * @returns {Promise<void>}
   */
  async function deleteInterestDomain(interestDomain: SelectedInterestDomain): Promise<void> {
    for (const interestName of interestDomain.selectedInterests) {
      removeCriterionLocally('Interests', interestName)
    }

    try {
      let lastResponse: QualificationCriteriaResponse | null = null
      for (const interestName of interestDomain.selectedInterests) {
        const body: QualificationCriteriaRequest = {
          category: QUALIFICATION_CRITERIA_CATEGORY_ENUM.Interests,
          value: interestName,
        }
        const { data } = await useFrontApi().delete<QualificationCriteriaResponse>(
          '/api/Profile/qualification/criteria',
          { data: body },
        )
        lastResponse = data
      }

      if (lastResponse) {
        setQualification(toQualificationSnapshot(lastResponse.profile))
        setSummary(lastResponse.summary ?? '')
      }
    } catch (error: unknown) {
      console.error('[agentInformationsStore] deleteInterestDomain:', error)
      await fetchProfile()
    }
  }

  const confidenceScorePct: ComputedRef<number> = computed((): number => {
    const isProfileSectionFilled: boolean[] = [
      locations.value.length > 0,
      interests.value.length > 0,
      academicLevel.value.length > 0,
      academicDegrees.value.length > 0,
      academicDuration.value.length > 0,
      academicStyles.value.length > 0,
      academicStartDate.value.length > 0,
      mbtiProfile.value.length > 0,
    ]

    const filledCount: number = isProfileSectionFilled.filter(Boolean).length
    return Math.round((filledCount / isProfileSectionFilled.length) * 100)
  })

  const confidenceScoreBaselinePct: Ref<number | null> = ref(null)

  /**
   * @param {number} score - Nouvelle baseline (mémoire + sessionStorage).
   * @returns {void}
   */
  function saveConfidenceScoreBaseline(score: number = confidenceScorePct.value): void {
    const rounded: number = Math.round(score)
    confidenceScoreBaselinePct.value = rounded
    writeConfidenceScoreBaseline(rounded)
  }

  let hasCapturedSessionBaseline: boolean = false

  watch(
    (): { score: number; storeStatus: StoreStatus } => ({
      score: confidenceScorePct.value,
      storeStatus: status.value,
    }),
    ({ score, storeStatus }: { score: number; storeStatus: StoreStatus }): void => {
      if (storeStatus !== 'ready') return
      if (hasCapturedSessionBaseline) return
      hasCapturedSessionBaseline = true

      const persistedBaseline: number | null = readConfidenceScoreBaseline()
      const sessionBaseline: number =
        persistedBaseline !== null ? Math.min(persistedBaseline, score) : score
      saveConfidenceScoreBaseline(sessionBaseline)
    },
    { immediate: true },
  )

  const confidenceScoreDelta: ComputedRef<number> = computed((): number => {
    const baseline: number | null = confidenceScoreBaselinePct.value
    if (baseline === null) return 0
    return Math.max(0, confidenceScorePct.value - baseline)
  })

  return {
    summary,
    locations,
    interests,
    academicLevel,
    academicDegrees,
    academicDuration,
    academicStyles,
    academicStartDate,
    mbtiProfile,
    degreesQuestion,
    stylesQuestion,
    unansweredCriteriaQuestions,
    selectedInterestDomains,
    filledCriteriaCategoryCount,
    remembered,
    dimensions,
    status,
    referentials,
    hasUnseenInterests,
    confidenceScorePct,
    confidenceScoreDelta,
    saveConfidenceScoreBaseline,
    setSummary,
    setQualification,
    applyQualificationFromLola,
    markInterestsAsSeen,
    applyDimensions,
    setRemembered,
    fetchProfile,
    fetchReferentials,
    addCriterion,
    deleteCriterion,
    deleteInterestDomain,
  }
})
